import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/MemberstackAuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Calendar,
  Users,
  FileText,
  Briefcase,
  Settings,
  LogOut,
  Sun,
  Moon,
  Monitor,
  ChevronDown,
  LayoutDashboard,
  HelpCircle,
} from 'lucide-react';

const navItems = [
  { to: '/app/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/app/calendar', icon: Calendar, label: 'Calendar' },
  { to: '/app/clients', icon: Users, label: 'Clients' },
  { to: '/app/invoices', icon: FileText, label: 'Invoices' },
  { to: '/app/services', icon: Briefcase, label: 'Services' },
  { to: '/app/settings', icon: Settings, label: 'Settings' },
  { to: '/app/help', icon: HelpCircle, label: 'Help & FAQ' },
];

function Sidebar() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const themeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;
  const ThemeIcon = themeIcon;

  return (
    <aside className="w-64 h-full border-r border-border bg-card/50 backdrop-blur-xl flex flex-col justify-between py-6 hidden lg:flex">
      <div>
        {/* Logo */}
        <div className="px-6 mb-8">
          <h1 className="text-2xl font-heading font-bold tracking-tight">Bõkr</h1>
          <p className="text-xs text-muted-foreground mt-1">{user?.business_name}</p>
        </div>

        {/* Navigation */}
        <nav className="px-3 space-y-1">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`
              }
              data-testid={`nav-${label.toLowerCase()}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Bottom section */}
      <div className="px-3 space-y-2">
        {/* Theme toggle */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full justify-start gap-3 text-muted-foreground" data-testid="theme-toggle">
              <ThemeIcon className="w-4 h-4" />
              <span className="text-sm">Theme</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-40">
            <DropdownMenuItem onClick={() => setTheme('light')} data-testid="theme-light">
              <Sun className="w-4 h-4 mr-2" /> Light
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme('dark')} data-testid="theme-dark">
              <Moon className="w-4 h-4 mr-2" /> Dark
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme('system')} data-testid="theme-system">
              <Monitor className="w-4 h-4 mr-2" /> System
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full justify-between text-muted-foreground" data-testid="user-menu">
              <span className="text-sm truncate">{user?.email}</span>
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.business_name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}

function MobileNav() {
  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-card border-t border-border flex justify-around items-center z-50 px-2">
      {navItems.slice(0, 4).map(({ to, icon: Icon, label }) => (
        <NavLink
          key={to}
          to={to}
          className={({ isActive }) =>
            `flex flex-col items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
              isActive
                ? 'text-primary'
                : 'text-muted-foreground'
            }`
          }
        >
          <Icon className="w-5 h-5" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppLayout() {
  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <main className="flex-1 overflow-hidden flex flex-col pb-16 lg:pb-0">
        <Outlet />
      </main>
      <MobileNav />
    </div>
  );
}
